// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Injectable } from '@angular/core';
import { HceEncryptionService } from '../hce-encryption/hce-encryption.service';
import { map, from, Observable, finalize, of, forkJoin, tap } from 'rxjs';
import { HceService } from '../hce.service';
import { Analysis } from '../../../interfaces/analysis';
import { HceAlignmentViewRequest } from '../../../interfaces/hce/hce-alignment-view';
import { HceSvService } from '../hce-sv/hce-sv.service';
import { HceAlignmentService } from '../hce-alignment/hce-alignment.service';
import { HceSvFilter } from '../../../interfaces/hce/hce-sv-filter';
import { HceAnnotation } from '../../../interfaces/hce/hce-annotation';
import { HceParserService } from '../hce-parser/hce-parser.service';

@Injectable({ providedIn: 'root' })
export class HceAlignmentViewService {

  // ===== Public delegated methods =====
  public readonly getUcscRefVersion: HceService['getUcscRefVersion'];
  public readonly openUcscGenomeBrowser: HceService['openUcscGenomeBrowser'];
  public readonly openAlignmentViewer: HceService['openAlignmentViewer'];
  public readonly openAssembleViewer: HceService['openAssembleViewer'];
  public readonly mapAlignmentViewToRequest: HceService['mapAlignmentViewToRequest'];
  public readonly collectFormEntries: HceService['collectFormEntries'];

  // ===== Internal state =====
  private _refsNameMapping: Record<string, string> = {}
  private annotationByFile: Record<string, HceAnnotation[]> = {};
  private buildOverlapCache = true;

  // ===== Accessors =====
  public get refsNameMapping(): Record<string, string> {
    return this._refsNameMapping;
  }

  public set refsNameMapping(value: Record<string, string>) {
    this._refsNameMapping = value;
  }

  public get annotationFiles(): string[] {
    return Object.keys(this.annotationByFile);
  }

  // ===== Constructor =====
  constructor(
    private hce: HceService,
    private enc: HceEncryptionService,
    private alignment: HceAlignmentService,
    private sv: HceSvService,
    private parser: HceParserService,
  ) {
    this.getUcscRefVersion = (...args) => this.hce.getUcscRefVersion(...args);
    this.openUcscGenomeBrowser = (...args) => this.hce.openUcscGenomeBrowser(...args);
    this.openAlignmentViewer = (...args) => this.hce.openAlignmentViewer(...args);
    this.openAssembleViewer = (...args) => this.hce.openAssembleViewer(...args);
    this.mapAlignmentViewToRequest = (...args) => this.hce.mapAlignmentViewToRequest(...args);
    this.collectFormEntries = (...args) => this.hce.collectFormEntries(...args);
  }

  // ===== Public API =====
  public getParams(token: string): Observable<HceAlignmentViewRequest> {
    return from(this.enc.decrypt(token)).pipe(
      map(text => {
        if (text == null) {
          throw new Error('Invalid token: decrypted value is null');
        }
        let raw: unknown;
        try {
          raw = JSON.parse(text);
        } catch (e) {
          throw new Error('Invalid token: JSON parse failed');
        }
        const params = raw as HceAlignmentViewRequest;
        if (!params.analysisId) {
          throw new Error('Invalid token: analysisId is missing');
        }
        return params;
      }),
    );
  }

  public getAnalysis(analysisId: number): Observable<Analysis> {
    return this.hce.getAnalysis(analysisId).pipe(
      map(response => {
        if (!response || response.success === false) {
          throw new Error(response?.message || 'Error while fetching the analysis.');
        }
        return response.data;
      }),
    );
  }

  public getAnnotationData(
    params: HceAlignmentViewRequest,
    analysis: Analysis,
  ): Observable<Record<string, HceAnnotation[]>> {
    console.time('Annotation data: download+transform');

    const annotationFileSet = new Set(params.annotationFile ?? []);
    
    return forkJoin({
      refsNameMap: this.hce.getRefsNameMapping(analysis.PanelID),
      annotations: annotationFileSet.size > 0
        ? this.hce.getAnnotationFile()
        : of<Record<string, string>>({}),
    }).pipe(
      map(({ refsNameMap, annotations }) => {
        const result = Object.fromEntries(
          Object.entries(annotations)
            .filter(([filename]) => annotationFileSet.has(filename))
            .map(([filename, text]) => {
              const parsed = this.parser.annotation.parse(
                filename,
                text,
                refsNameMap,
              );
              return [filename, parsed];
            })
            .filter(([_, parsed]) => parsed?.length > 0)
        );
        this.refsNameMapping = refsNameMap;
        this.annotationByFile = result;
        return result;
      }),
      finalize(() => console.timeEnd('Annotation data: download+transform'))
    );
  }

  public getAlignmentData(
    analysis: Analysis,
    chromosomename: string,
    contigId: number | null,
  ): Observable<any> {
    console.time('Alignment data: download+transform');
    return this.alignment.getAlignmentData(
      analysis,
      chromosomename,
      contigId,
      this.annotationByFile,
    ).pipe(
      finalize(() => console.timeEnd('Alignment data: download+transform'))
    );
  }

  public getSvData(analysis: Analysis, filter: HceSvFilter): Observable<any[]> {
    console.time('SV data: download+transform');
    return this.sv.getSvData(
      analysis,
      filter,
      this.annotationByFile,
      {
        useRefsNameMapping: true,
        useColorMap: true,
        buildOverlapCache: this.buildOverlapCache
      }
    ).pipe(
      tap(() => this.buildOverlapCache = false),
      finalize(() => console.timeEnd('SV data: download+transform'))
    );
  }

  public getAssembleData(analysisId: number, contigId: number) {
    console.time('Assemble data: download');
    return this.hce.getAssembleJson(analysisId, contigId).pipe(
      finalize(() => console.timeEnd('Assemble data: download'))
    );
  }
}
